import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { loginSchema } from '../../../components/utilities/models/gen-models';
import { useNavigate } from 'react-router-dom';
import axios from '../../../components/utilities/axios';
import { getDefaultRoute } from '../../../components/utilities/default-route';
import LandingHeader from '../landing-page/landing-header/LandingHeader';
import '../login/loginStyles.css';
import { CCol, CContainer, CForm, CFormInput, CImage, CRow } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilLockLocked, cilUser } from '@coreui/icons';
import secureMeLogo from '../../../assets/images/new-securemepp-logo.jpeg';

const SecureMeLogin = () => {
  const { register, handleSubmit, formState } = useForm({
    resolver: yupResolver(loginSchema),
  });
  const { errors, isSubmitting } = formState;
  const [loginError, setLoginError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [passwordShown, setPasswordShown] = useState(false);
  const [networkError, setNetworkError] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (details) => {
    setLoginError(false);
    setNetworkError('');
    try {
      const { data } = await axios.post('/registrations/secureMeLoginEmail', details);
      if (data.success) {
        localStorage.setItem('y-z-main', 'true');
        localStorage.setItem('y-z-access', data.token);
        localStorage.setItem('userId', data.userDetails.userId);
        localStorage.setItem('userData', JSON.stringify(data.userDetails));
        const defaultRoute = (await getDefaultRoute(data.userDetails.roleId)) || '/dashboard';
        navigate(defaultRoute, { state: { ...data } });
        return;
      }
      setLoginError(true);
      setErrorMessage(data.message || 'Invalid login details.');
    } catch (error) {
      setNetworkError(error.response?.data?.message || 'Unable to sign in right now. Please try again.');
    }
  };

  return (
    <main className="login-page login-page--app">
      <LandingHeader menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <CContainer fluid className="login-container">
        <CRow className="justify-content-center">
          <CCol xl={10} lg={9} md={11} sm={12}>
            <div className="login-shell">
              <section className="login-brand-panel">
                <div className="login-brand-content">
                  <div className="login-brand-mark">
                    <CImage src={secureMeLogo} alt="SecureMe" className="login-brand-logo" />
                  </div>
                  <p className="login-brand-eyebrow">SecureMe</p>
                  <h1>Keep every person connected and protected.</h1>
                  <p className="login-brand-copy">
                    Monitor movement, respond to alerts, and keep your team safe from one secure workspace.
                  </p>
                  <div className="login-brand-points">
                    <div className="login-brand-point">
                      <span className="login-point-dot" />
                      <span>Real-time location visibility</span>
                    </div>
                    <div className="login-brand-point">
                      <span className="login-point-dot" />
                      <span>Clear alerts when attention is needed</span>
                    </div>
                    <div className="login-brand-point">
                      <span className="login-point-dot" />
                      <span>Simple tools for your safety team</span>
                    </div>
                  </div>
                </div>
                <div className="login-brand-footer">SecureMe Platform</div>
              </section>

              <section className="login-form-panel">
                <div className="login-form-wrap">
                  <div className="login-form-heading">
                    <span className="login-form-eyebrow">Welcome back</span>
                    <h2>Sign in to SecureMe</h2>
                    <p>Enter your details to access your safety workspace.</p>
                  </div>

                  <CForm onSubmit={handleSubmit(handleLogin)} className="login-form">
                    <div className="login-field">
                      <label htmlFor="emailAddress">Email address</label>
                      <div className="login-input-wrap">
                        <CIcon icon={cilUser} className="login-input-icon" />
                        <CFormInput
                          id="emailAddress"
                          type="email"
                          name="emailAddress"
                          placeholder="you@example.com"
                          autoComplete="username"
                          className="login-input"
                          invalid={Boolean(errors.emailAddress)}
                          {...register('emailAddress')}
                        />
                      </div>
                      {errors.emailAddress?.message && (
                        <span className="login-field-error">{errors.emailAddress.message}</span>
                      )}
                    </div>

                    <div className="login-field">
                      <label htmlFor="password">Password</label>
                      <div className="login-input-wrap">
                        <CIcon icon={cilLockLocked} className="login-input-icon" />
                        <CFormInput
                          id="password"
                          type={passwordShown ? 'text' : 'password'}
                          name="password"
                          placeholder="Enter your password"
                          autoComplete="current-password"
                          className="login-input login-input-with-action"
                          invalid={Boolean(errors.password)}
                          {...register('password')}
                        />
                        <button
                          type="button"
                          className="login-password-toggle"
                          onClick={() => setPasswordShown((visible) => !visible)}
                          aria-label={passwordShown ? 'Hide password' : 'Show password'}
                        >
                          <i className={passwordShown ? 'bi bi-eye-slash' : 'bi bi-eye'} />
                        </button>
                      </div>
                      {errors.password?.message && (
                        <span className="login-field-error">{errors.password.message}</span>
                      )}
                    </div>

                    {loginError && <div className="login-alert login-alert-error">{errorMessage}</div>}
                    {networkError && <div className="login-alert login-alert-error">{networkError}</div>}

                    <button className="login-submit" type="submit" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <span className="login-spinner" aria-hidden="true" />
                          Signing you in...
                        </>
                      ) : (
                        'Sign in'
                      )}
                    </button>
                  </CForm>
                </div>
              </section>
            </div>
          </CCol>
        </CRow>
      </CContainer>
    </main>
  );
};

export default SecureMeLogin;
